<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>
    <transition name="bounce">
      <b-container fluid>
        <b-row align-h="between" align-v="center">
          <b-col cols="12" md="5" lg="4" class="p-3 mb-0">
            <b-form-input
              v-model="search"
              placeholder="Search name, email, phone or trade..."
              debounce="500"
            />
          </b-col>
        </b-row>

        <!-- Mobile: compact list rows (hidden on md+) -->
        <div class="d-md-none pb-2">
          <div
            v-for="item in resumes"
            :key="item.id"
            class="mobile-resume-item"
          >
            <div class="d-flex align-items-center">
              <div class="flex-grow-1" style="min-width: 0">
                <span class="mobile-resume__name">{{
                  (item.tradie && item.tradie.full_name) || '-'
                }}</span>
                <div class="mobile-resume__meta">
                  {{ (item.tradie && item.tradie.email) || '-'
                  }}{{
                    item.tradie && item.tradie.trade
                      ? ' · ' + item.tradie.trade
                      : ''
                  }}
                </div>
                <div class="mobile-resume__date">
                  {{ formatSharedAt(item.shared_at) }}
                </div>
              </div>
              <div class="d-flex align-items-center mobile-resume__actions">
                <b-button
                  variant="primary"
                  class="mobile-resume__action-btn"
                  :disabled="!item.url"
                  :title="
                    item.url ? 'Open File in new tab' : 'Resume not available'
                  "
                  @click="viewResume(item.url)"
                >
                  <b-icon icon="box-arrow-up-right" />
                </b-button>
                <b-button
                  variant="outline-light"
                  class="mobile-resume__action-btn"
                  title="Remove Resume"
                  @click="removingResume(true, item.id)"
                >
                  <b-icon-x-circle-fill variant="danger" />
                </b-button>
              </div>
            </div>
          </div>
          <div
            v-if="!resumes || !resumes.length"
            class="text-center py-4 text-muted"
          >
            No resumes found.
          </div>
        </div>

        <!-- Desktop: full table (hidden below md) -->
        <div class="d-none d-md-block">
          <b-table
            id="my-table"
            class="mt-4"
            responsive
            hover
            show-empty
            ref="resumesTable"
            :fields="fields"
            :items="resumes"
            tbody-tr-class="cursor-pointer"
            @sort-changed="onSortChanged"
          >
            <template #table-busy>
              <div class="text-center text-primary my-2">
                <digger-loader />
              </div>
            </template>

            <template #cell(actions)="{ item }">
              <div class="d-flex align-items-center flex-nowrap">
                <!-- Button - View Resume -->
                <b-button
                  variant="primary"
                  style="width: 32px; height: 32px; padding: 0 0 2px 4px"
                  :disabled="!item.url"
                  :title="
                    item.url ? 'Open File in new tab' : 'Resume not available'
                  "
                  @click="viewResume(item.url)"
                >
                  <b-icon icon="box-arrow-up-right" size="sm" />
                </b-button>

                <!-- Button - Remove -->
                <b-button
                  variant="outline-light"
                  style="height: 32px"
                  class="px-2 ml-2"
                  title="Remove Resume"
                  @click="removingResume(true, item.id)"
                >
                  <b-icon-x-circle-fill variant="danger" />
                </b-button>
              </div>
            </template>
          </b-table>
        </div>

        <b-pagination
          v-if="rows > perPage"
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :current-page="currentPage"
          aria-controls="my-table"
          align="center"
        />

        <p v-if="resumesMeta" align="center">
          Showing {{ resumes.length }} out of total of
          {{ resumesMeta.total }} resumes
        </p>

        <b-modal v-model="isRemovingResume" centered size="md" @ok="onOk">
          <template #modal-title>
            <h3 class="font-weight-bold">Remove Resume</h3>
          </template>

          <b-row class="mb-4">
            <b-col>Remove Tradie's Resume from this Company?</b-col>
          </b-row>

          <template #modal-footer>
            <div class="remove-resume-actions d-flex">
              <b-button
                variant="secondary"
                class="modal-btn"
                @click="removingResume(false)"
                >CANCEL</b-button
              >
              <b-button
                variant="primary"
                class="modal-btn"
                @click="onRemoveResume"
                >YES</b-button
              >
            </div>
          </template>
        </b-modal>

        <FilePreviewModal
          v-if="resumePreviewUrl"
          :visible="!!resumePreviewUrl"
          :url="resumePreviewUrl"
          mime-type="application/pdf"
          name="Resume.pdf"
          @close="resumePreviewUrl = ''"
        />
      </b-container>
    </transition>
  </b-overlay>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FilePreviewModal from '@/components/modals/FilePreviewModal.vue'

export default {
  components: { FilePreviewModal },

  data() {
    return {
      // The resume being previewed, or '' when the preview is closed.
      resumePreviewUrl: '',
      perPage: 10,
      currentPage: 1,
      search: '',
      sortBy: null,
      sortDir: null,

      isLoading: false,
      isRemovingResume: false,
      resumeId: null,

      fields: [
        {
          key: 'tradie.full_name',
          label: 'Full Name',
          sortable: true,
        },
        {
          key: 'tradie.email',
          label: 'Email',
          sortable: true,
        },
        {
          key: 'tradie.phone_number',
          label: 'Phone Number',
          sortable: true,
        },
        {
          key: 'tradie.trade',
          label: 'Trade',
          sortable: true,
        },
        {
          key: 'shared_at',
          label: 'Shared At',
          sortable: true,
          formatter: (value) => {
            return value
              ? new Intl.DateTimeFormat('en-AU', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }).format(new Date(value)) +
                  ' at ' +
                  new Intl.DateTimeFormat('en-AU', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                  })
                    .format(new Date(value))
                    .replace(/(\d+) (am|pm)/i, '$1$2')
                    .toLowerCase()
              : '-'
          },
        },
        'actions',
      ],
    }
  },

  computed: {
    ...mapGetters('company', ['resumes', 'resumesMeta']),

    rows() {
      return this.resumesMeta ? this.resumesMeta.total : 0
    },
  },

  watch: {
    currentPage: async function (newCurrentPage) {
      this.currentPage = newCurrentPage
      await this.fetchResumes()
    },

    search: async function (newSearch) {
      this.search = newSearch
      if (this.currentPage !== 1) {
        this.currentPage = 1
      } else {
        await this.fetchResumes()
      }
    },
  },

  methods: {
    ...mapActions('company', {
      getResumes: 'getResumes',
      removeResume: 'removeResume',
    }),
    // PLACE HOLDER
    onOk() {
      // Handle when OK button is clicked
      this.onRemoveResume()
    },

    async fetchResumes() {
      this.isLoading = true

      await this.getResumes({
        page: this.currentPage,
        limit: this.perPage,
        search: this.search,
        sortBy: this.sortBy,
        sortDir: this.sortDir,
      })
        .catch((err) => console.log(err))
        .finally(() => (this.isLoading = false))
    },

    async onSortChanged(sort) {
      this.sortBy = sort.sortBy
      this.sortDir = sort.sortDesc ? 'DESC' : 'ASC'

      await this.fetchResumes()
    },

    showToast(title, message, variant = 'success') {
      this.$bvToast.toast(message, {
        title: title,
        autoHideDelay: 3000,
        variant: variant,
      })
    },

    viewResume(url) {
      // The button is disabled whenever url is falsy, but guard here too —
      // window.open(null, ...) gets coerced to the string "null" and the
      // browser resolves that as a relative path (/null), landing on a 404.
      if (!url) {
        this.showToast(
          'Resume not available',
          'This resume file is not currently available.',
          'danger',
        )
        return
      }
      this.resumePreviewUrl = url
    },

    removingResume(status, id = null) {
      this.isRemovingResume = status
      this.resumeId = id
    },

    async onRemoveResume() {
      await this.removeResume(this.resumeId)
        .then(() => {
          this.fetchResumes()
          this.removingResume(false)
        })
        .catch((err) => console.log(err))
    },

    handleError(err) {
      this.showToast('Oops.. Something went wrong!', err.data.message, 'danger')
    },

    formatSharedAt(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
  },

  async mounted() {
    await this.fetchResumes()
  },
}
</script>

<style lang="scss" scoped>
/deep/ .cursor-pointer {
  cursor: pointer;
}

.mobile-resume-item {
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #e1e6ea;
  background: white;
}

.mobile-resume__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #364052;
  line-height: 1.3;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-resume__meta {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-resume__date {
  font-size: 0.72rem;
  color: #8b95a3;
  margin-top: 0.1rem;
}

.mobile-resume__actions {
  gap: 0.3rem;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.mobile-resume__action-btn {
  height: 28px;
  width: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  flex-shrink: 0;
}

/* Remove Resume modal actions: a consistent pair — right-aligned with a set
   min-width on larger screens, and equal full-width halves on phones. Matches
   the Add New Project modal's footer treatment.
   Width is set to 100% because this div sits inside BootstrapVue's own
   .modal-footer (which is itself justify-content: flex-end) — without it
   the div just hugs its own content and gets pushed to the right, so the
   flex-grow on the buttons below has no free width to actually grow into. */
.remove-resume-actions {
  gap: 0.5rem;
  width: 100%;
  justify-content: flex-end;
}
.modal-btn {
  min-width: 110px;
  font-weight: 600;
}

@media (max-width: 575.98px) {
  .remove-resume-actions {
    justify-content: stretch;
  }
  .modal-btn {
    flex: 1 1 0;
    min-width: 0;
    height: 44px;
  }
}
</style>
